import { useState, useEffect, useRef } from "react";
import { api } from "../App";

export default function MentionTextarea({ value, onChange, onSubmit, placeholder = "", rows = 2, className = "" }) {
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionPos, setMentionPos] = useState({ top: 0, left: 0 });
  const [selectedIdx, setSelectedIdx] = useState(0);
  const textareaRef = useRef(null);
  const containerRef = useRef(null);

  const handleInput = (e) => {
    const val = e.target.value;
    onChange(val);
    const cursorPos = e.target.selectionStart;
    const textBefore = val.substring(0, cursorPos);
    const match = textBefore.match(/@(\w*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setSelectedIdx(0);
      const ta = textareaRef.current;
      if (ta) {
        const lines = textBefore.split("\n");
        setMentionPos({ top: lines.length * 22 + 4, left: 8 });
      }
    } else {
      setMentionQuery(null);
      setMentionResults([]);
    }
  };

  useEffect(() => {
    if (mentionQuery === null) { setMentionResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/users/search?q=${encodeURIComponent(mentionQuery)}`);
        setMentionResults(res.data || []);
      } catch (e) { console.error(e); }
    }, 200);
    return () => clearTimeout(timer);
  }, [mentionQuery]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setMentionQuery(null);
        setMentionResults([]);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (user) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const cursorPos = ta.selectionStart;
    const textBefore = value.substring(0, cursorPos);
    const textAfter = value.substring(cursorPos);
    const atIdx = textBefore.lastIndexOf("@");
    if (atIdx === -1) return;
    const newText = textBefore.substring(0, atIdx) + `@${user.name} ` + textAfter;
    onChange(newText);
    setMentionQuery(null);
    setMentionResults([]);
    setTimeout(() => {
      const newPos = atIdx + user.name.length + 2;
      ta.setSelectionRange(newPos, newPos);
      ta.focus();
    }, 0);
  };

  const handleKeyDown = (e) => {
    // Mention dropdown open — arrow/enter for selection
    if (mentionResults.length > 0 && mentionQuery !== null) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, mentionResults.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter") { e.preventDefault(); handleSelect(mentionResults[selectedIdx]); return; }
      if (e.key === "Escape") { e.preventDefault(); setMentionQuery(null); setMentionResults([]); return; }
    }
    // No dropdown — Enter submits, Shift+Enter = newline
    if (e.key === "Enter" && !e.shiftKey && onSubmit && mentionQuery === null) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div ref={containerRef} className="relative flex-1">
      <textarea ref={textareaRef} value={value} onChange={handleInput} onKeyDown={handleKeyDown}
        placeholder={placeholder} rows={rows}
        className={className || "form-input resize-none"} />
      {mentionResults.length > 0 && (
        <div className="absolute z-[60] glass-card rounded-xl overflow-hidden shadow-2xl max-h-44 overflow-y-auto w-72"
          style={{ bottom: "100%", left: `${mentionPos.left}px`, marginBottom: "4px" }}
          data-testid="mention-dropdown">
          <div className="px-3 py-1.5 text-[12px] text-white/20 border-b border-white/5">People</div>
          {mentionResults.map((u, i) => (
            <button key={u.user_id} type="button" onClick={() => handleSelect(u)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${i === selectedIdx ? "bg-white/[0.08]" : "hover:bg-white/[0.04]"}`}>
              <div className="w-7 h-7 rounded-full bg-[var(--r)]/15 flex items-center justify-center text-[12px] text-[#E31937] font-bold flex-shrink-0">
                {(u.name || "?")[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/90 text-[13px] truncate normal-case">{u.name}</p>
                <p className="text-[var(--t3)] text-[13px] truncate normal-case">{u.designation || u.role}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
